import React, { useEffect, useState, useCallback } from "react";
import { api, API, errMsg, fmtNum, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, PackageCheck, Truck, Camera, PenLine, AlertTriangle } from "lucide-react";

export default function ProofOfDelivery() {
  const { hasPerm } = useAuth();
  const [dos, setDos] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [dispatch, setDispatch] = useState(null);
  const [pod, setPod] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, del, v, dr, rt] = await Promise.all([
        api.get("/delivery-orders"), api.get("/deliveries"),
        api.get("/vehicles").catch(() => ({ data: [] })),
        api.get("/drivers").catch(() => ({ data: [] })),
        api.get("/routes").catch(() => ({ data: [] })),
      ]);
      setDos(d.data); setDeliveries(del.data); setVehicles(v.data); setDrivers(dr.data); setRoutes(rt.data);
    } catch (e) { toast.error(errMsg(e)); setDos([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const canDispatch = hasPerm("logistics.manage");
  const canPod = hasPerm("delivery.record");
  const delByDo = {};
  deliveries.forEach((x) => { delByDo[x.do_no] = x; });

  const doDispatch = async () => {
    setBusy(true);
    try {
      await api.post(`/delivery-orders/${dispatch.id}/dispatch`, {
        vehicle_id: dispatch.vehicle_id, driver_id: dispatch.driver_id, route_id: dispatch.route_id });
      toast.success("Dispatched"); setDispatch(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const doPod = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("recipient", pod.recipient || "");
      if (pod.pod_quantity !== undefined && pod.pod_quantity !== "") fd.append("pod_quantity", pod.pod_quantity);
      if (pod.signature) fd.append("signature", pod.signature);
      if (pod.photo) fd.append("photo", pod.photo);
      const { data } = await api.post(`/deliveries/${pod.deliveryId}/pod`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.reconcile?.flagged) toast.warning(`POD variance flagged: ${data.reconcile.variance_pct}% exceeds ${data.reconcile.tolerance_pct}% tolerance — exception raised`);
      else toast.success("Proof of Delivery captured"); setPod(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!dos) return <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;

  return (
    <div>
      <div className="mb-5">
        <h1 className="font-head text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><PackageCheck className="h-6 w-6 text-[#F47920]" /> Dispatch & Proof of Delivery</h1>
        <p className="text-sm text-slate-500 mt-0.5">Assign vehicle, driver and route to delivery orders, then capture digital signature and photo proof of delivery.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="pf-table">
            <TableHeader><TableRow className="bg-slate-50 hover:bg-slate-50">
              {["DO No.", "Customer", "Qty (L)", "Vehicle", "Driver", "Route", "Status", "POD", "Actions"].map((h) =>
                <TableHead key={h} className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold py-2.5 px-3">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {dos.length === 0 ? <TableRow><TableCell colSpan={9} className="py-10 text-center text-slate-400 text-sm">No delivery orders.</TableCell></TableRow>
                : dos.map((d) => {
                  const del = delByDo[d.do_no];
                  return (
                    <TableRow key={d.id} data-testid={`pod-do-${d.id}`} className="border-slate-100">
                      <TableCell className="py-2 px-3 mono text-xs font-semibold">{d.do_no}</TableCell>
                      <TableCell className="py-2 px-3 text-[13px]">{d.customer_name}</TableCell>
                      <TableCell className="py-2 px-3 text-[13px] tabular">{fmtNum(d.quantity)}</TableCell>
                      <TableCell className="py-2 px-3 text-xs mono">{d.vehicle || "—"}</TableCell>
                      <TableCell className="py-2 px-3 text-[13px]">{d.driver || "—"}</TableCell>
                      <TableCell className="py-2 px-3 text-[13px]">{d.route || "—"}</TableCell>
                      <TableCell className="py-2 px-3"><StatusBadge status={d.status} /></TableCell>
                      <TableCell className="py-2 px-3">
                        {del?.pod_captured ? (
                          <div className="flex items-center gap-1.5">
                            {del.pod_signature_path && <a href={`${API}/pod/${del.id}/signature`} target="_blank" rel="noreferrer" title="Signature" data-testid={`pod-sig-${del.id}`}><PenLine className="h-4 w-4 text-emerald-600" /></a>}
                            {del.pod_photo_path && <a href={`${API}/pod/${del.id}/photo`} target="_blank" rel="noreferrer" title="Photo" data-testid={`pod-photo-${del.id}`}><Camera className="h-4 w-4 text-emerald-600" /></a>}
                            {del.pod_reconciled === false && <span title={`POD variance ${del.pod_variance_pct}% (signed ${del.pod_quantity})`} data-testid={`pod-flag-${del.id}`}><AlertTriangle className="h-4 w-4 text-rose-500" /></span>}
                            {del.pod_reconciled === true && <span className="text-[10px] text-emerald-600 font-semibold" title={`Signed ${del.pod_quantity}`}>✓ matched</span>}
                          </div>
                        ) : <span className="text-slate-300 text-xs">—</span>}
                      </TableCell>
                      <TableCell className="py-2 px-3 whitespace-nowrap text-right">
                        {canDispatch && d.status === "Issued" && (
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs ml-1" onClick={() => setDispatch({ id: d.id })} data-testid={`btn-dispatch-${d.id}`}><Truck className="h-3.5 w-3.5 mr-1" /> Dispatch</Button>
                        )}
                        {canPod && del && !del.pod_captured && (
                          <Button size="sm" className="h-7 px-2 text-xs ml-1 bg-[#12346B] hover:bg-[#12346B]/90 text-white border-0" onClick={() => setPod({ deliveryId: del.id, recipient: del.recipient })} data-testid={`btn-pod-${del.id}`}><Camera className="h-3.5 w-3.5 mr-1" /> Capture POD</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!dispatch} onOpenChange={(o) => !o && setDispatch(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Dispatch Delivery Order</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Vehicle / Tanker</Label>
              <Select onValueChange={(v) => setDispatch((s) => ({ ...s, vehicle_id: v }))}>
                <SelectTrigger data-testid="dispatch-vehicle"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                <SelectContent className="bg-white z-[60]">{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.reg_no} · {v.type}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Driver</Label>
              <Select onValueChange={(v) => setDispatch((s) => ({ ...s, driver_id: v }))}>
                <SelectTrigger data-testid="dispatch-driver"><SelectValue placeholder="Select driver" /></SelectTrigger>
                <SelectContent className="bg-white z-[60]">{drivers.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Route</Label>
              <Select onValueChange={(v) => setDispatch((s) => ({ ...s, route_id: v }))}>
                <SelectTrigger data-testid="dispatch-route"><SelectValue placeholder="Select route" /></SelectTrigger>
                <SelectContent className="bg-white z-[60]">{routes.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDispatch(null)}>Cancel</Button>
            <Button className="bg-[#12346B] hover:bg-[#12346B]/90" onClick={doDispatch} disabled={busy} data-testid="btn-confirm-dispatch">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Dispatch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pod} onOpenChange={(o) => !o && setPod(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Capture Proof of Delivery</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Recipient Name</Label>
              <Input value={pod?.recipient || ""} onChange={(e) => setPod((s) => ({ ...s, recipient: e.target.value }))} data-testid="pod-recipient" /></div>
            <div className="grid gap-1.5"><Label className="text-xs text-slate-600">POD Signed Quantity (L)</Label>
              <Input type="number" placeholder="Quantity confirmed by recipient" value={pod?.pod_quantity ?? ""} onChange={(e) => setPod((s) => ({ ...s, pod_quantity: e.target.value }))} data-testid="pod-quantity" />
              <span className="text-[11px] text-slate-400">Auto-flagged if it differs from the delivery order beyond the configured tolerance.</span></div>
            <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Signature Image</Label>
              <Input type="file" accept="image/*" onChange={(e) => setPod((s) => ({ ...s, signature: e.target.files[0] }))} data-testid="pod-signature-input" /></div>
            <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Delivery Photo</Label>
              <Input type="file" accept="image/*" onChange={(e) => setPod((s) => ({ ...s, photo: e.target.files[0] }))} data-testid="pod-photo-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPod(null)}>Cancel</Button>
            <Button className="bg-[#12346B] hover:bg-[#12346B]/90" onClick={doPod} disabled={busy} data-testid="btn-confirm-pod">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save POD</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
